import * as api from "@/api";
import styles from "@/components/migration-popup.module.css";
import PopupShell from "@/components/popup-shell";
import * as signals from "@/signals";
import { MigrationStatus } from "@/types";
import { getSupportLink } from "@/utils";
import {
	faChampagneGlasses,
	faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t } from "i18next";
import { renderToStaticMarkup } from "preact-render-to-string";
import { JSX, useEffect, useRef, useState } from "react";

interface MigrationPopupProps {
	isShown: boolean;
}

function MigrationPopup({ isShown }: MigrationPopupProps): JSX.Element {
	const [currentStatus, setCurrentStatus] = useState<MigrationStatus>(
		MigrationStatus.NOT_STARTED,
	);
	const [queuePosition, setQueuePosition] = useState<number>(0);

	const intervalId = useRef<number | null>(null);

	const checkMigration = async (): Promise<void> => {
		try {
			const data = await api.getMigrationStatus();
			setCurrentStatus(data.status);
			if (data.queuePosition) {
				setQueuePosition(data.queuePosition);
			}
			if (data.status === MigrationStatus.DONE) {
				window.clearInterval(intervalId.current);
			}
		} catch (error) {
			await api.handleApiError(error);
		}
	};

	const closeMigrationPopup = (): void => {
		window.location.reload();
	};

	const handleStartClick = async (): Promise<void> => {
		signals.isLoadingShown.value = true;
		try {
			await api.startMigration();
			await checkMigration();
			intervalId.current = window.setInterval(() => {
				void checkMigration();
			}, 5000);
		} catch (error) {
			await api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
		}
	};

	useEffect(() => {
		if (!isShown) {
			if (intervalId.current) {
				window.clearInterval(intervalId.current);
			}
			return;
		}
		void checkMigration();
		return (): void => {
			if (intervalId.current) {
				window.clearInterval(intervalId.current);
			}
		};
	});

	return (
		<PopupShell
			isCloseDisabled={true}
			isHeadingHidden={true}
			isPureWhite={true}
			isShown={isShown}
			onClose={closeMigrationPopup}
			title={t("migrate")}
		>
			<div className={styles["container"]}>
				<FontAwesomeIcon
					className={styles["icon"]}
					icon={faChampagneGlasses}
				/>
				<h1>
					{currentStatus === MigrationStatus.DONE
						? t("everythingIsReady")
						: t("almostThere")}
				</h1>
				<div className={styles["description"]}>
					{currentStatus === MigrationStatus.DONE
						? t("migrationDoneDescription")
						: t("migrationDescription")}
				</div>
				{currentStatus === MigrationStatus.QUEUED ||
				currentStatus === MigrationStatus.IN_PROGRESS ? (
					<>
						<div className={styles["progress-text"]}>
							{currentStatus === MigrationStatus.QUEUED
								? t("queuing", { queuePosition })
								: t("updatingPleaseWait")}
						</div>
						<div className={styles["progress-bar-wrapper"]}>
							<div className={styles["progress-bar"]}></div>
						</div>
					</>
				) : (
					<div className={styles["button-container"]}>
						{currentStatus === MigrationStatus.ERROR && (
							<div className={styles["error-info"]}>
								<FontAwesomeIcon icon={faTriangleExclamation} />
								<div
									dangerouslySetInnerHTML={{
										__html: t("errorOccurred", {
											contactUs: renderToStaticMarkup(
												<a
													className="link"
													href={getSupportLink()}
													rel="noreferrer"
													target="_blank"
												>
													{t("contactUs")}
												</a>,
											),
										}),
									}}
								></div>
							</div>
						)}
						{(currentStatus === MigrationStatus.NOT_STARTED ||
							currentStatus === MigrationStatus.ERROR) && (
							<button
								className={styles["button"]}
								onClick={() => void handleStartClick()}
								type="button"
							>
								{currentStatus === MigrationStatus.NOT_STARTED
									? t("start")
									: t("retry")}
							</button>
						)}
						{currentStatus === MigrationStatus.DONE && (
							<button
								className={styles["button"]}
								onClick={closeMigrationPopup}
								type="button"
							>
								{t("done")}
							</button>
						)}
					</div>
				)}
			</div>
		</PopupShell>
	);
}

export default MigrationPopup;
